/**
 * The username a person picks at signup becomes their public resume
 * subdomain: https://<username>.skillsnap.me/. It must therefore be a single
 * DNS label. Mirrors infrastructure/lambdas/shared/public_handle.py
 * (`validate_new_username`) — keep the two in sync.
 *
 * - letters, numbers and hyphens only (a period would create a second host
 *   label the *.skillsnap.me certificate cannot cover; underscores are not
 *   legal hostname characters and strict clients reject them)
 * - 3-30 characters, starts with a letter, no trailing hyphen
 * - case-insensitive (published lowercase)
 */
export const USERNAME_RULE_MESSAGE =
  'Usernames may use letters, numbers and hyphens only (no periods, underscores or spaces).';

export const NEW_USERNAME_MIN_LEN = 3;
export const NEW_USERNAME_MAX_LEN = 30;

const HANDLE_RE = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

export const RESERVED_HANDLES: ReadonlySet<string> = new Set([
  'www', 'app', 'api', 'admin', 'mail', 'email', 'smtp', 'imap', 'pop', 'ftp',
  'blog', 'help', 'support', 'docs', 'status', 'cdn', 'static', 'assets',
  'fonts', 'skins', 'user', 'users', 'system', 'root', 'auth', 'login',
  'signup', 'account', 'billing', 'jobs', 'snappy', 'skillsnap', 'demo',
  'example', 'test', 'dev', 'staging', 'prod', 'localhost',
]);

/** Error message for an invalid NEW username, or null when it is acceptable. */
export function validateNewUsername(raw: string | null | undefined): string | null {
  const h = (raw || '').trim().toLowerCase();
  if (!h) return 'Username is required';
  if (/\s/.test(raw || '')) return USERNAME_RULE_MESSAGE;
  if (h.length < NEW_USERNAME_MIN_LEN) return `Username must be at least ${NEW_USERNAME_MIN_LEN} characters`;
  if (h.length > NEW_USERNAME_MAX_LEN) return `Username must be at most ${NEW_USERNAME_MAX_LEN} characters`;
  if (!/^[a-z]/.test(h)) return 'Username must start with a letter';
  if (!HANDLE_RE.test(h)) return USERNAME_RULE_MESSAGE;
  if (RESERVED_HANDLES.has(h)) return 'That username is reserved';
  return null;
}
